<template>
  <div class="staggerCountdownView">
    <div class="header">
      <div v-if="avatarDefaultIdleAnimations.length && screenerAvatar" class="avatar-container">
        <AvatarComponent
          :options="{ position: 'dynamic', size: 'height', audio: '' }"
          :inSession="false"
          :isAnimating="true"
        />
      </div>
      <h1>
        Your screener will {{ actionVerb }} in <span class="countdown">{{ countdownText }}</span>
      </h1>
      <p class="help">
        Another pupil in your class is on the same question — we're spacing things out so the
        questions don't overlap. Hang tight, the screener will continue automatically.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

// Components
import AvatarComponent from '@/components/AvatarComponent.vue'

// Stores
import { useEffectsStore } from '@/stores/EffectsStore'
import { useDataStore } from '@/stores/DataStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

const emit = defineEmits<{
  (e: 'stagger-ended'): void
}>()

const dataStore = useDataStore()
const effectsStore = useEffectsStore()
const activeScreenerStore = useActiveScreenerStore()
const { screenerAvatar } = storeToRefs(dataStore)
const { avatarDefaultIdleAnimations } = storeToRefs(effectsStore)
const { staggerEndsAt, pendingStaggerAction } = storeToRefs(activeScreenerStore)

const actionVerb = computed(() => (pendingStaggerAction.value === 'resume' ? 'continue' : 'start'))

const nowTick = ref(Date.now())
let interval: ReturnType<typeof setInterval> | null = null

const countdownText: ComputedRef<string> = computed(() => {
  if (!staggerEndsAt.value) return '0:00'
  const remainingMs = Math.max(0, staggerEndsAt.value - nowTick.value)
  const minutes = Math.floor(remainingMs / 60000)
  const seconds = Math.floor((remainingMs % 60000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

onMounted(() => {
  nowTick.value = Date.now()
  interval = setInterval(() => {
    nowTick.value = Date.now()
    if (staggerEndsAt.value && nowTick.value >= staggerEndsAt.value) {
      clearTickInterval()
      emit('stagger-ended')
    }
  }, 250)
})

onBeforeUnmount(() => {
  clearTickInterval()
})

function clearTickInterval() {
  if (interval) {
    clearInterval(interval)
    interval = null
  }
}
</script>

<style lang="scss">
.staggerCountdownView {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: left;

  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0;
    gap: 2rem;
    max-width: calc(var(--full-vw) * 0.75);

    h1 {
      text-align: center;
      .countdown {
        color: $primary;
      }
    }

    .help {
      color: $secondary;
      text-align: center;
      max-width: 32rem;
      margin: 0;
    }
  }

  .avatar-container {
    height: calc(var(--full-vh) * 0.4);
  }
}
</style>
